import type { AuthenticatedUserFetchedAction } from '@Application/Security/Authentications/Actions/authenticatedUserFetched';
import type { AuthenticatedUserFetchFailedAction } from '@Application/Security/Authentications/Actions/authenticatedUserFetchFailed';
import type { SecondFactorEnrolledAction } from '@Application/Security/Authentications/Actions/secondFactorEnrolled';
import type { SecondFactorEnrollmentRequiredAction } from '@Application/Security/Authentications/Actions/secondFactorEnrollmentRequired';
import type { SecondFactorRequiredAction } from '@Application/Security/Authentications/Actions/secondFactorRequired';
import type { SecondFactorVerifiedAction } from '@Application/Security/Authentications/Actions/secondFactorVerified';
import type { SessionExpiredAction } from '@Application/Security/Authentications/Actions/sessionExpired';
import type { UserAuthenticatedAction } from '@Application/Security/Authentications/Actions/userAuthenticated';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import AuthenticatedUserView from '@UI/Store/AuthenticatedUser/Views/AuthenticatedUserView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

/** `'unknown'` is the state before `GET /api/me` has answered — it is never a resting state. */
export type SessionStatus =
	| 'unknown'
	| 'anonymous'
	| 'mfa_required'
	| 'mfa_enrollment_required'
	| 'authenticated';

export type AuthenticatedUserStore = {
	user: AuthenticatedUserView | null;
	sessionStatus: SessionStatus;
};

const initialState: AuthenticatedUserStore = {
	user: null,
	sessionStatus: 'unknown',
};

type Actions =
	| UserAuthenticatedAction
	| SecondFactorRequiredAction
	| SecondFactorEnrollmentRequiredAction
	| SecondFactorVerifiedAction
	| SecondFactorEnrolledAction
	| AuthenticatedUserFetchedAction
	| AuthenticatedUserFetchFailedAction
	| UserRevokedAction
	| SessionExpiredAction;

const whenUserAuthenticated = (
	store: AuthenticatedUserStore,
	action: UserAuthenticatedAction,
): AuthenticatedUserStore => ({
	...store,
	user: new AuthenticatedUserView(action.payload),
	sessionStatus: 'authenticated',
});

const whenSecondFactorRequired = (store: AuthenticatedUserStore): AuthenticatedUserStore => ({
	...store,
	user: null,
	sessionStatus: 'mfa_required',
});

const whenSecondFactorEnrollmentRequired = (store: AuthenticatedUserStore): AuthenticatedUserStore => ({
	...store,
	user: null,
	sessionStatus: 'mfa_enrollment_required',
});

const whenSecondFactorVerified = (
	store: AuthenticatedUserStore,
	action: SecondFactorVerifiedAction,
): AuthenticatedUserStore => ({
	...store,
	user: new AuthenticatedUserView(action.payload.user),
	sessionStatus: 'authenticated',
});

const whenSecondFactorEnrolled = (
	store: AuthenticatedUserStore,
	action: SecondFactorEnrolledAction,
): AuthenticatedUserStore => ({
	...store,
	user: new AuthenticatedUserView(action.payload.user),
	sessionStatus: 'authenticated',
});

const whenAuthenticatedUserFetched = (
	store: AuthenticatedUserStore,
	action: AuthenticatedUserFetchedAction,
): AuthenticatedUserStore => ({
	...store,
	user: new AuthenticatedUserView(action.payload),
	sessionStatus: 'authenticated',
});

// Any failed probe means "no usable session" for the caller, whether the server answered 401 (no
// session) or the request failed outright (network error, 500): none of these leave a session the
// UI could act on, so they all resolve to the same anonymous state.
const whenAuthenticatedUserFetchFailed = (): AuthenticatedUserStore => ({
	user: null,
	sessionStatus: 'anonymous',
});

// Distinct from the probe-failure handler: the session is known to be lost, so no re-probe is wanted.
const whenSessionExpired = (): AuthenticatedUserStore => ({
	user: null,
	sessionStatus: 'anonymous',
});

const whenUserRevoked = (): AuthenticatedUserStore => initialState;

export default registerStore<'authenticatedUserStore', Actions>(
	'authenticatedUserStore',
	createSliceReducer({
		USER_AUTHENTICATED: whenUserAuthenticated,
		SECOND_FACTOR_REQUIRED: whenSecondFactorRequired,
		SECOND_FACTOR_ENROLLMENT_REQUIRED: whenSecondFactorEnrollmentRequired,
		SECOND_FACTOR_VERIFIED: whenSecondFactorVerified,
		SECOND_FACTOR_ENROLLED: whenSecondFactorEnrolled,
		AUTHENTICATED_USER_FETCHED: whenAuthenticatedUserFetched,
		AUTHENTICATED_USER_FETCH_FAILED: whenAuthenticatedUserFetchFailed,
		USER_REVOKED: whenUserRevoked,
		SESSION_EXPIRED: whenSessionExpired,
	}),
	initialState,
);
